import { fetchExternalWebhookMethods } from "@/lib/api/resources/externalWebhooks";
import {
  ExternalWebhook,
  ExternalWebhookMethod,
} from "@rumble-it/common-lib/types/models/ExternalWebhook.interface";
import { UseMutationResult, useQuery } from "@tanstack/react-query";
import { Button, Select } from "@rumbledirect/ui";
import { Card, Input } from "@rumbledirect/ui";
import { Form, message } from "antd";
import { useMemo } from "react";

interface ExternalWebhookFormCardProps {
  mutation: UseMutationResult<null, Error, ExternalWebhook, unknown>;
  loading?: boolean;
  initialValues?: ExternalWebhook;
  title?: string;
  buttonText?: string;
}

export const ExternalWebhookFormCard = ({
  mutation,
  loading,
  initialValues,
  title = "Externe webhook aanmaken",
  buttonText = "Aanmaken",
}: ExternalWebhookFormCardProps) => {
  const initialFormValues = useMemo(() => {
    if (!initialValues) return undefined;

    return {
      ...initialValues,
      configuration:
        typeof initialValues.configuration === "string"
          ? initialValues.configuration
          : JSON.stringify(initialValues.configuration ?? {}, null, 2),
    };
  }, [initialValues]);

  const externalWebhookMethodsQuery = useQuery({
    queryKey: ["externalWebhookMethods"],
    queryFn: fetchExternalWebhookMethods,
    select: (data) =>
      data.member.map((option: ExternalWebhookMethod) => ({
        label: option.name,
        value: option.value,
      })),
  });

  const handleFinish = async (
    values: Partial<ExternalWebhook> & { configuration?: string },
  ) => {
    // Parse configuration to object if it's a string
    let parsedConfiguration: unknown = values.configuration;
    if (
      typeof values.configuration === "string" &&
      values.configuration.trim() !== ""
    ) {
      try {
        parsedConfiguration = JSON.parse(values.configuration as string);
      } catch {
        message.error("Configuratie moet geldige JSON zijn");
        return;
      }
    }

    const payload: ExternalWebhook = {
      ...values,
      configuration: parsedConfiguration as ExternalWebhook["configuration"],
    } as ExternalWebhook;

    mutation.mutate(payload);
  };

  return (
    <Card loading={loading} title={title}>
      <Form
        onFinish={handleFinish}
        layout="vertical"
        initialValues={initialFormValues}
      >
        <Form.Item
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
          label="Titel"
          name="title"
        >
          <Input />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
          label="Url"
          name="url"
        >
          <Input />
        </Form.Item>
        <Form.Item initialValue={"POST"} label="Method" name="method">
          <Select
            options={externalWebhookMethodsQuery.data}
            loading={externalWebhookMethodsQuery.isFetching}
          />
        </Form.Item>
        <Form.Item
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
          label="Configuratie"
          name="configuration"
        >
          <Input.TextArea rows={10} />
        </Form.Item>
        <Form.Item label={null}>
          <Button loading={mutation.isPending} type="primary" htmlType="submit">
            {buttonText}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
